import React, { useRef, useEffect, useMemo, useState } from 'react';
import JSZip from 'jszip';
import * as d3 from 'd3';
import * as THREE from 'three';
import { useLoadingTracker } from '../contexts/LoadingTrackerContext';
import { modelCache } from '../Utils/modelCache';
import { getOriginENR } from '../config/siteConfig';
import { getModelArrayBuffer } from '../Utils/offlineCache';
import SelectedHoleHighlight from '../clickTools/SelectedHoleHighlight.js';
import { useHoleDisplayRadii } from '../store/holeDisplayStore';
import { fitInstancedMeshBounds } from '../Utils/instancedBounds';
import { holeTraceColor } from '../Utils/holeTraceColor';

export default function HolesLoader({ url, dataset, holetype, visible, name, onLoadComplete, onLoadFailed }) {
    const [holeData, setHoleData] = useState([]);
    const [, setLoading] = useState(true);
    const [, setError] = useState(null);
    const color = new THREE.Color();
    const constants_ = getOriginENR();
    const { startLoading, stopLoading, markAsFailed, clearFailed, updateProgress } = useLoadingTracker();
    const { lineWidth } = useHoleDisplayRadii();
    const objectName = name || `Holes_${dataset}_${holetype}`;

    useEffect(() => {
        let cancelled = false;
        let hasMarkedFailed = false;
        
        const cacheKey = `holes_${url}_${dataset}_${holetype}`;
        
        // Check cache first
        const cachedData = modelCache.get(cacheKey);
        if (cachedData) {
            setHoleData(cachedData);
            setLoading(false);
            setError(null);
            stopLoading(objectName);
            onLoadComplete?.(objectName);
            return;
        }

        // Check if already loading
        const existingPromise = modelCache.getLoadingPromise(cacheKey);
        if (existingPromise) {
            existingPromise
                .then((result) => {
                    if (!cancelled && result) {
                        setHoleData(result);
                        setLoading(false);
                        setError(null);
                        stopLoading(objectName);
                        onLoadComplete?.(objectName);
                    }
                })
                .catch((err) => {
                    if (!cancelled) {
                        setError(err);
                        setLoading(false);
                        stopLoading(objectName);
                        onLoadFailed?.(objectName);
                    }
                });
            return () => {
                cancelled = true;
            };
        }
        
        // Start tracking loading
        startLoading(objectName);

        const fetchData = async (attempt = 0) => {
            if (cancelled) return null;

            try {
                setLoading(true);
                setError(null);
                
                const blob = await fetchDataWithProgress(url, attempt, (info) => {
                    updateProgress(objectName, info);
                });
                if (cancelled) return null;

                updateProgress(objectName, { phase: 'parsing', percent: 100, indeterminate: false });
                
                //console.log('Download complete. Extracting...');
                const zip = await JSZip.loadAsync(blob);
                if (cancelled) return null;
                
                const fileName = Object.keys(zip.files)[0]; // Assuming there's only one file in the zip
                const fileData = await zip.file(fileName).async('string');
                if (cancelled) return null;
                
                const parsedData = d3.csvParse(fileData);

                // Filter rows based on dataset and holetype, then order by Depth_From and holeID
                const filteredData = parsedData
                    .filter(row => row.DataSet === dataset && row.Hole_Type === holetype)
                    .sort((a, b) => a.Hole_ID.localeCompare(b.Hole_ID) || a.Depth_From - b.Depth_From);

                if (!cancelled) {
                    // Cache the loaded data
                    modelCache.set(cacheKey, filteredData);
                    modelCache.removeLoadingPromise(cacheKey);
                    
                    setHoleData(filteredData);
                    setLoading(false);
                    setError(null);
                    stopLoading(objectName);
                    clearFailed(objectName); // Clear failed status on success
                    onLoadComplete?.(objectName);
                    if (attempt > 0) {
                        console.log(`[HolesLoader] Successfully loaded ${url} after ${attempt + 1} attempt(s) and cached`);
                    } else {
                        console.log(`[HolesLoader] Successfully loaded ${url} and cached`);
                    }
                    return filteredData;
                }
                return null;
            } catch (err) {
                if (cancelled) return null;

                // Check if it's a retryable error
                const errorMessage = err?.message || err?.toString() || String(err) || '';
                const errorString = errorMessage.toLowerCase();
                const errorName = (err?.name || '').toLowerCase();
                const errorType = (err?.type || '').toLowerCase();

                // Check for HTTP2 protocol errors and network errors
                const isHttp2Error = 
                    errorString.includes('http2') ||
                    errorString.includes('err_http2_protocol_error') ||
                    errorString.includes('protocol_error') ||
                    errorName.includes('http2') ||
                    errorType.includes('http2');

                const isNetworkError = 
                    errorString.includes('failed to fetch') ||
                    errorString.includes('networkerror') ||
                    errorString.includes('network') ||
                    errorString.includes('aborted') ||
                    errorString.includes('timeout') ||
                    errorString.includes('load failed') ||
                    errorString.includes('could not load') ||
                    errorString.includes('fetch') ||
                    errorName.includes('network') ||
                    errorName.includes('abort') ||
                    errorType === 'networkerror' ||
                    errorType === 'abort';

                const isRetryableError = isHttp2Error || isNetworkError;

                // Mark as failed after 5 attempts to show in UI (but continue retrying in background)
                if (attempt >= 5 && !hasMarkedFailed) {
                  hasMarkedFailed = true;
                  markAsFailed(objectName, err, () => {
                    hasMarkedFailed = false;
                    modelCache.delete(cacheKey);
                    setError(null);
                    setLoading(true);
                    startLoading(objectName);
                    const newPromise = fetchData(0);
                    modelCache.setLoadingPromise(cacheKey, newPromise);
                  }, { backgroundRetry: true });
                }

                // Always retry network/HTTP2 errors indefinitely
                // For other errors, retry up to 100 times as a safety measure
                if (isRetryableError || attempt < 100) {
                    // Cap exponential backoff at 30 seconds
                    const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
                    const errorInfo = errorMessage || errorName || 'Unknown error';
                    console.warn(`[HolesLoader] Attempt ${attempt + 1} failed for ${url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
                    
                    // Keep loading state true while retrying
                    setLoading(true);
                    setError(null);
                    
                    // Exponential backoff with cap
                    await new Promise(resolve => setTimeout(resolve, backoffDelay));
                    
                    // Continue retrying
                    return fetchData(attempt + 1);
                }
                
                // Only set error if we've truly exhausted retries (shouldn't happen for network errors)
                modelCache.removeLoadingPromise(cacheKey);
                setError(err);
                setLoading(false);
                console.error(`[HolesLoader] Failed to load ${url} after ${attempt + 1} attempts:`, err);
                onLoadFailed?.(objectName);
                throw err; // Re-throw to be caught by promise handler
            }
        };

        // Start loading and create promise
        const loadingPromise = fetchData();
        modelCache.setLoadingPromise(cacheKey, loadingPromise);

        return () => {
            cancelled = true;
            stopLoading(objectName);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, dataset, holetype, objectName, startLoading, stopLoading, markAsFailed, clearFailed, updateProgress]);

    const groupedData = useMemo(() => {
        const grouped = {};
        holeData.forEach(row => {
            const { Hole_ID } = row;

            if (!grouped[Hole_ID]) grouped[Hole_ID] = [];
            grouped[Hole_ID].push(row);
        });

        return grouped;
    }, [holeData]);

    const instances = useMemo(() => {
        const colorsArray = [];
        const posArray = [];
        const detArray = [];

        Object.keys(groupedData).forEach(holeID => {
            const holeRows = groupedData[holeID];
            holeRows.forEach(({ X, Y, Z, Depth_From, Depth_To, Au_ppm }) => {
                if (Au_ppm > 0.5) {
                    if (Au_ppm >= 5) {
                        color.set(0x800080); // PURPLE
                    } else if (Au_ppm >= 2.5) {
                        color.set(0xb45f06); // BROWN
                    } else if (Au_ppm >= 1.5) {
                        color.set(0xcc0000); // RED
                    } else if (Au_ppm >= 0.75) {
                        color.set(0x38761d); // GREEN
                    } else {
                        color.set(0xf1c232); // YELLOW
                    }
                    colorsArray.push([color.r, color.g, color.b]);
                    posArray.push([Number(X) - constants_[0], Number(Z) - constants_[2], constants_[1] - Number(Y)]);
                    detArray.push({ holeID, from: Depth_From, to: Depth_To, grade: Au_ppm });
                }
            });
        });

        return { colors: colorsArray, positions: posArray, details: detArray };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedData]);

    const traceBatches = useMemo(
        () => buildTraceBatches(groupedData, constants_),
        [groupedData]
    );

    if (!visible) return null;

    return (
        <>
            <group visible={visible} name={name} userData={{ mdvLayerKey: name }}>
                <HoleTraceBatches batches={traceBatches} grouped={groupedData} layerName={name} />
                <group>
                    {instances.positions.length > 0 && (
                        <Assays count={instances.positions.length} holes={instances} id={`${dataset}_${holetype}_Assay`} />
                    )}
                </group>
                <SelectedHoleHighlight
                    holes={groupedData}
                    getLinePoints={(id) => {
                        const rows = groupedData[id];
                        if (!rows?.length) return null;
                        return [
                            [Number(rows[0].NAT_East) - constants_[0], Number(rows[0].NAT_RL) - constants_[2], constants_[1] - Number(rows[0].NAT_North)],
                            ...rows.map(({ X, Y, Z }) => [Number(X) - constants_[0], Number(Z) - constants_[2], constants_[1] - Number(Y)])
                        ];
                    }}
                />
            </group>
        </>
    );
}

const fetchDataWithProgress = async (url, _attempt = 0, onProgress) => {
    const buffer = await getModelArrayBuffer(url, { onProgress });
    return new Blob([buffer]);
};

function buildTraceBatches(grouped, origin) {
    const [east, north, rl] = origin;
    const byColor = new Map();
    Object.keys(grouped).forEach((holeID) => {
        const rows = grouped[holeID];
        if (!rows?.length) return;
        const color = holeTraceColor(rows[0]);
        let batch = byColor.get(color);
        if (!batch) {
            batch = { color, positions: [], holeIds: [] };
            byColor.set(color, batch);
        }
        const pts = [
            Number(rows[0].NAT_East) - east,
            Number(rows[0].NAT_RL) - rl,
            north - Number(rows[0].NAT_North),
        ];
        rows.forEach(({ X, Y, Z }) => {
            pts.push(Number(X) - east, Number(Z) - rl, north - Number(Y));
        });
        const nPts = pts.length / 3;
        for (let i = 0; i < nPts - 1; i += 1) {
            batch.positions.push(
                pts[i * 3], pts[i * 3 + 1], pts[i * 3 + 2],
                pts[(i + 1) * 3], pts[(i + 1) * 3 + 1], pts[(i + 1) * 3 + 2]
            );
            batch.holeIds.push(holeID);
        }
    });
    return Array.from(byColor.values()).map((b) => ({
        color: b.color,
        positions: new Float32Array(b.positions),
        holeIds: b.holeIds,
    }));
}

function holeTraceRaycast(raycaster, intersects) {
    const before = intersects.length;
    THREE.LineSegments.prototype.raycast.call(this, raycaster, intersects);
    for (let i = before; i < intersects.length; i += 1) {
        intersects[i].instanceId = Math.floor((intersects[i].index ?? 0) / 2);
    }
}

function HoleTraceBatches({ batches, grouped, layerName }) {
    return batches.map((batch) => (
        <lineSegments
            key={`${layerName}_${batch.color}`}
            frustumCulled
            name={layerName}
            userData={{
                mdvLayerKey: layerName,
                pickInstance: (id) => grouped[batch.holeIds[id]]?.[0] || null,
            }}
            raycast={holeTraceRaycast}
        >
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[batch.positions, 3]} />
            </bufferGeometry>
            <lineBasicMaterial color={batch.color} />
        </lineSegments>
    ));
}

function Assays({ count, holes, id }) {
    const ref = useRef();
    const [colors, setColors] = useState(new Float32Array());
    const [instancesData, setInstancesData] = useState([]);
    const { assaySphere } = useHoleDisplayRadii();

    useEffect(() => {
        let colorsArray = [];
        let idata = [];
        for (let i = 0; i < count; i++) {
            const assayPosition = holes.positions[i];
            const color = holes.colors[i];
            const data = holes.details[i];

            idata.push(data);
            const assayMatrix = new THREE.Matrix4().makeTranslation(assayPosition[0], assayPosition[1], assayPosition[2]);
            ref.current.setMatrixAt(i, assayMatrix);
            colorsArray.push(color[0], color[1], color[2]);
        }
        setInstancesData(idata);
        setColors(Float32Array.from(colorsArray));
        ref.current.instanceMatrix.needsUpdate = true;
        fitInstancedMeshBounds(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [holes]);

    return (
        <instancedMesh ref={ref} args={[null, null, count]} userData={{ instancesData }} name={id}>
            <sphereGeometry key={assaySphere} args={[assaySphere, 4, 4]}>
                <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
            </sphereGeometry>
            <meshLambertMaterial vertexColors toneMapped={false} />
        </instancedMesh>
    );
}
